import { useState } from "react";
import { Sparkles, Quote, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const STAGES = [
  ["been", "What's been", "What has helped and what you've lived through"],
  ["happening", "What's happening", "What's present for you now"],
  ["possible", "What's possible", "What you'd like to experience"],
  ["next", "What's next", "Small steps that fit your life"],
];

// The person's own words, grouped by journey stage, kept visibly separate from any AI suggestion.
export function SaidSections({ said = [] }) {
  if (!said.length) return <p className="text-sm text-[#8FA89B]" data-testid="reflection-said-empty">You skipped the written questions — that's completely fine.</p>;
  return (
    <div className="space-y-6" data-testid="reflection-said">
      {STAGES.map(([key, label, hint]) => {
        const secs = said.filter((s) => s.stage === key);
        if (!secs.length) return null;
        return (
          <div key={key} data-testid={`reflection-stage-${key}`}>
            <p className="eyw-label !text-[#D98E73]">{label}</p>
            <p className="text-xs text-[#8FA89B] mb-2">{hint}</p>
            {secs.map((s) => (
              <div key={s.slot} className="mb-3">
                <p className="text-sm font-semibold text-[#1A242B]">{s.title}</p>
                <ul className="mt-1 space-y-1.5">
                  {s.items.map((i) => (
                    <li key={i.id} className="text-sm text-[#4A5D6B] flex gap-2"><Quote size={13} className="text-[#8FA89B] mt-1 shrink-0" />
                      <span><span className="text-[#1A242B]">“{i.answer}”</span><span className="block text-xs text-[#8FA89B]">{i.question}</span></span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

export function SuggestionBox({ suggestion, loading }) {
  if (loading) return <div className="rounded-2xl border border-dashed border-[#8FA89B]/50 p-5 text-sm text-[#4A5D6B] flex items-center gap-2" data-testid="reflection-suggestion-loading"><Loader2 size={15} className="animate-spin" /> Writing a gentle suggestion from your words…</div>;
  if (!suggestion) return null;
  return (
    <div className="rounded-2xl border border-[#8FA89B]/40 bg-[#8FA89B]/[0.07] p-5" data-testid="reflection-suggestion">
      <p className="eyw-label flex items-center gap-1.5 !text-[#5C8A72]"><Sparkles size={13} /> AI suggestion</p>
      <p className="text-xs text-[#8FA89B] mb-3">{suggestion.label}</p>
      {suggestion.connections?.length > 0 && (
        <ul className="space-y-2 mb-3">{suggestion.connections.map((c, i) => <li key={i} className="text-sm text-[#1A242B] leading-relaxed">{c}</li>)}</ul>
      )}
      {suggestion.next_steps?.length > 0 && (
        <>
          <p className="text-sm font-semibold text-[#1A242B] mt-2">Possible next steps</p>
          <ul className="list-disc pl-5 text-sm text-[#4A5D6B] space-y-1 mt-1">{suggestion.next_steps.map((c, i) => <li key={i}>{c}</li>)}</ul>
        </>
      )}
      {suggestion.option_fits?.length > 0 && (
        <>
          <p className="text-sm font-semibold text-[#1A242B] mt-3">Why some options below may fit</p>
          <ul className="text-sm text-[#4A5D6B] space-y-1 mt-1" data-testid="reflection-option-fits">{suggestion.option_fits.map((f, i) => <li key={i}><b className="text-[#144F75]">{f.title}</b>: {f.why}</li>)}</ul>
        </>
      )}
    </div>
  );
}

// Members can correct the reflection, name an intention and choose their next step. Earlier versions are kept.
export function MemberReflectionForm({ explorationId, initial = {}, suggestions = [], onSaved }) {
  const [form, setForm] = useState({ correction: initial.correction || "", intention: initial.intention || "", chosen_next_step: initial.chosen_next_step || "" });
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(!initial.updated_at);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    try { await api.put(`/me/gauge/explorations/${explorationId}/reflection`, form); toast.success("Saved to your journey"); setOpen(false); onSaved?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (!open) return (
    <div className="rounded-2xl bg-white border border-[#E5E0D8] p-5" data-testid="member-reflection-view">
      <div className="flex justify-between items-start gap-3">
        <div className="space-y-1 text-sm text-[#4A5D6B]">
          {form.intention && <p><b className="text-[#1A242B]">My intention:</b> {form.intention}</p>}
          {form.chosen_next_step && <p><b className="text-[#1A242B]">My next step:</b> {form.chosen_next_step}</p>}
          {form.correction && <p><b className="text-[#1A242B]">What I'd change in this reflection:</b> {form.correction}</p>}
        </div>
        <button onClick={() => setOpen(true)} aria-label="Edit my reflection" data-testid="member-reflection-edit" className="text-[#144F75]"><Pencil size={15} /></button>
      </div>
    </div>
  );
  return (
    <div className="rounded-2xl bg-white border border-[#E5E0D8] p-5 space-y-3" data-testid="member-reflection-form">
      <p className="font-serif text-lg text-[#1A242B]">Make it yours</p>
      <label className="block text-sm text-[#4A5D6B]">My intention, in my words
        <input value={form.intention} onChange={set("intention")} data-testid="member-intention" className="eyw-input w-full mt-1" placeholder="e.g. Protect one quiet evening a week" />
      </label>
      <label className="block text-sm text-[#4A5D6B]">One next step I choose
        <input value={form.chosen_next_step} onChange={set("chosen_next_step")} list={`ns-${explorationId}`} data-testid="member-next-step" className="eyw-input w-full mt-1" placeholder="Something small enough to begin" />
        <datalist id={`ns-${explorationId}`}>{suggestions.map((s) => <option key={s} value={s} />)}</datalist>
      </label>
      <label className="block text-sm text-[#4A5D6B]">Anything in this reflection that doesn't fit? (optional)
        <textarea rows={2} value={form.correction} onChange={set("correction")} data-testid="member-correction" className="eyw-input w-full mt-1 resize-none" />
      </label>
      <button onClick={save} disabled={busy} data-testid="member-reflection-save" className="eyw-btn !py-2 text-sm disabled:opacity-60">{busy && <Loader2 size={14} className="animate-spin" />} Save to my journey</button>
    </div>
  );
}

const ZONES = [
  ["above", "Above", "Inspiration & Possibility", "#D98E73"],
  ["center", "Center", "Presence & Participation", "#144F75"],
  ["below", "Below", "Impact & Stewardship", "#5C8A72"],
];

function CompassDial({ counts }) {
  const max = Math.max(1, ...Object.values(counts));
  const y = { above: 34, center: 70, below: 106 };
  return (
    <svg viewBox="0 0 140 140" className="w-36 h-36 shrink-0" role="img" aria-label="Alignment Compass: where your words landed">
      <circle cx="70" cy="70" r="64" fill="none" stroke="#E5E0D8" strokeWidth="1.5" />
      <circle cx="70" cy="70" r="40" fill="none" stroke="#E5E0D8" strokeDasharray="3 4" />
      <line x1="70" y1="6" x2="70" y2="134" stroke="#E5E0D8" />
      {ZONES.map(([k, , , color]) => (
        <g key={k}>
          <circle cx="70" cy={y[k]} r={6 + (counts[k] / max) * 12} fill={color} fillOpacity={counts[k] ? 0.22 : 0.05} stroke={color} strokeOpacity={counts[k] ? 0.8 : 0.2} />
          <text x="70" y={y[k] + 4} textAnchor="middle" fontSize="11" fill={color} fontWeight="600">{counts[k] || ""}</text>
        </g>
      ))}
      <text x="70" y="16" textAnchor="middle" fontSize="8" fill="#8FA89B">ABOVE</text>
      <text x="70" y="131" textAnchor="middle" fontSize="8" fill="#8FA89B">BELOW</text>
    </svg>
  );
}

// Alignment Compass: places the person's own answers Above / Center / Below. A map of their words, not a rating.
export function AlignmentCompass({ said = [] }) {
  const items = said.flatMap((s) => s.items.map((i) => ({ ...i, compass: i.compass || "center" })));
  if (!items.length) return null;
  const counts = Object.fromEntries(ZONES.map(([k]) => [k, items.filter((i) => i.compass === k).length]));
  return (
    <div className="rounded-2xl border border-[#E5E0D8] bg-white p-5" data-testid="alignment-compass">
      <p className="font-serif text-xl text-[#1A242B]">Your Alignment Compass</p>
      <p className="text-xs text-[#8FA89B] mb-4">Where your words landed this time. It's a map for reflection, not a score — move anything that feels misplaced in your own notes.</p>
      <div className="flex flex-col sm:flex-row gap-5 items-center sm:items-start">
        <CompassDial counts={counts} />
        <ul className="flex-1 space-y-3 min-w-0 w-full">
          {ZONES.map(([k, label, sub, color]) => {
            const zone = items.filter((i) => i.compass === k).slice(0, 2);
            return (
              <li key={k} data-testid={`compass-${k}`}>
                <p className="text-sm font-semibold" style={{ color }}>{label} · <span className="font-normal text-[#4A5D6B]">{sub}</span></p>
                {zone.length ? zone.map((i) => <p key={i.id} className="text-sm text-[#1A242B] truncate">“{i.answer}”</p>)
                  : <p className="text-xs text-[#8FA89B]">Nothing here yet — that's okay.</p>}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
